import type { ComponentType } from 'react'

export enum EFinancingConsultingStep {
  FORM = 'FORM',
  LOGIN = 'LOGIN',
  REGISTRATION = 'REGISTRATION',
  AFTER_FORM_SUBMIT = 'AFTER_FORM_SUBMIT',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export type TFinancingRequestStepperStartLoginHandler = () => void
export type TFinancingRequestStepperFinishLoginHandler = () => void
export type TFinancingRequestStepperStartRegistrationHandler = () => void
export type TFinancingRequestStepperFinishRegistrationHandler<TForm extends Record<string, unknown>> = (formValues: TForm) => void
export type TFinancingRequestStepperSuccessFormSubmitHandler = (financingRequestId: number) => void
export type TFinancingRequestStepperErrorFormSubmitHandler = () => void
export type TFinancingRequestStepperFormResetHandler = () => void
export type TFinancingRequestStepperAfterFormSubmitHandler = (financingRequestId: number) => void

export type TFinancingRequestStepperCallbacks<TForm extends Record<string, unknown>> = {
  onStartLogin: TFinancingRequestStepperStartLoginHandler
  onFinishLogin: TFinancingRequestStepperFinishLoginHandler
  onStartRegistration: TFinancingRequestStepperStartRegistrationHandler
  onFinishRegistration: TFinancingRequestStepperFinishRegistrationHandler<TForm>
  onSuccess: TFinancingRequestStepperSuccessFormSubmitHandler
  onError: TFinancingRequestStepperErrorFormSubmitHandler
  onResetForm: TFinancingRequestStepperFormResetHandler
  onAfterFormSubmit: TFinancingRequestStepperAfterFormSubmitHandler
}

export type TFinancingRequestStepperFormState<TForm extends Record<string, unknown>> = {
  step: EFinancingConsultingStep.FORM
  initialFormValues?: TForm
}

export type TFinancingRequestStepperLoginState = {
  step: EFinancingConsultingStep.LOGIN
}

export type TFinancingRequestStepperRegistrationState = {
  step: EFinancingConsultingStep.REGISTRATION
}

export type TFinancingRequestStepperAfterFormSubmitState = {
  step: EFinancingConsultingStep.AFTER_FORM_SUBMIT
  fundingRequestId: number
}

export type TFinancingRequestStepperSuccessState = {
  step: EFinancingConsultingStep.SUCCESS
  fundingRequestId: number
}

export type TFinancingRequestStepperErrorState = {
  step: EFinancingConsultingStep.ERROR
}

export type TFinancingRequestStepperState<TForm extends Record<string, unknown>> =
  | TFinancingRequestStepperFormState<TForm>
  | TFinancingRequestStepperLoginState
  | TFinancingRequestStepperRegistrationState
  | TFinancingRequestStepperAfterFormSubmitState
  | TFinancingRequestStepperSuccessState
  | TFinancingRequestStepperErrorState

export type TFinancingRequestStepperFormComponentProps<TForm extends Record<string, unknown>> = {
  initialFormValues?: Partial<TForm>
  state: TFinancingRequestStepperFormState<TForm>
} & TFinancingRequestStepperCallbacks<TForm>
export type TFinancingRequestStepperFormComponent<TForm extends Record<string, unknown>> = ComponentType<
  TFinancingRequestStepperFormComponentProps<TForm>
>

export type TFinancingRequestStepperLoginComponentProps<TForm extends Record<string, unknown>> = {
  state: TFinancingRequestStepperLoginState
} & TFinancingRequestStepperCallbacks<TForm>
export type TFinancingRequestStepperLoginComponent<TForm extends Record<string, unknown>> = ComponentType<
  TFinancingRequestStepperLoginComponentProps<TForm>
>

export type TFinancingRequestStepperRegistrationComponentProps<TForm extends Record<string, unknown>> = {
  state: TFinancingRequestStepperRegistrationState
} & TFinancingRequestStepperCallbacks<TForm>
export type TFinancingRequestStepperRegistrationComponent<TForm extends Record<string, unknown>> = ComponentType<
  TFinancingRequestStepperRegistrationComponentProps<TForm>
>

export type TFinancingRequestStepperAfterFormSubmitComponentProps<TForm extends Record<string, unknown>> = {
  state: TFinancingRequestStepperAfterFormSubmitState
} & TFinancingRequestStepperCallbacks<TForm>
export type TFinancingRequestStepperAfterFormSubmitComponent<TForm extends Record<string, unknown>> = ComponentType<
  TFinancingRequestStepperAfterFormSubmitComponentProps<TForm>
>

export type TFinancingRequestStepperSuccessComponentProps<TForm extends Record<string, unknown>> = {
  state: TFinancingRequestStepperSuccessState
} & TFinancingRequestStepperCallbacks<TForm>
export type TFinancingRequestStepperSuccessComponent<TForm extends Record<string, unknown>> = ComponentType<
  TFinancingRequestStepperSuccessComponentProps<TForm>
>

export type TFinancingRequestStepperErrorComponentProps<TForm extends Record<string, unknown>> = {
  state: TFinancingRequestStepperErrorState
} & TFinancingRequestStepperCallbacks<TForm>
export type TFinancingRequestStepperErrorComponent<TForm extends Record<string, unknown>> = ComponentType<
  TFinancingRequestStepperErrorComponentProps<TForm>
>

export type TFinancingRequestStepperProps<TForm extends Record<string, unknown>> = {
  initialFormValues?: Partial<TForm>
  FormComponent: TFinancingRequestStepperFormComponent<TForm>
  LoginComponent: TFinancingRequestStepperLoginComponent<TForm>
  RegistrationComponent: TFinancingRequestStepperRegistrationComponent<TForm>
  AfterFormSubmitComponent?: TFinancingRequestStepperAfterFormSubmitComponent<TForm>
  SuccessComponent: TFinancingRequestStepperSuccessComponent<TForm>
  ErrorComponent: TFinancingRequestStepperErrorComponent<TForm>
}
